import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import type {
  CompanyDocumentView, DocumentScope, DocumentSeedResult, DocumentTypeView, EmployeeDocumentView,
} from '../types'

const TYPES = '/v1/document-types'

export function useDocumentTypes(scope?: DocumentScope) {
  return useQuery({
    queryKey: ['documents', 'types', scope],
    queryFn: async () =>
      (await apiClient.get<DocumentTypeView[]>(TYPES, { params: { scope } })).data,
  })
}

export function useDocumentTypeMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['documents'] })

  return {
    create: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<DocumentTypeView>(TYPES, body)).data,
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) =>
        (await apiClient.put<DocumentTypeView>(`${TYPES}/${id}`, body)).data,
      onSuccess: invalidate,
    }),
    deactivate: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<DocumentTypeView>(`${TYPES}/${id}/deactivate`)).data,
      onSuccess: invalidate,
    }),
    seedUae: useMutation({
      mutationFn: async (legalEntityId: string) =>
        (await apiClient.post<DocumentSeedResult>(`${TYPES}/seed-uae`, { legalEntityId })).data,
      onSuccess: invalidate,
    }),
  }
}

export function useEmployeeDocuments(employeeId: string | undefined) {
  return useQuery({
    queryKey: ['documents', 'employee', employeeId],
    enabled: employeeId != null,
    queryFn: async () =>
      (await apiClient.get<EmployeeDocumentView[]>(`/v1/employees/${employeeId}/documents`)).data,
  })
}

/**
 * Fetches one document *including its number*.
 *
 * Deliberately a mutation rather than a query, even though it is a GET. Every call is
 * written to the sensitive-access log, and a query would refetch on window focus and on
 * cache invalidation — turning one deliberate act into an access record nobody performed.
 */
export function useRevealDocumentNumber() {
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) =>
      (await apiClient.get<EmployeeDocumentView>(`/v1/documents/${id}`, { params: { reason } })).data,
  })
}

export function useEmployeeDocumentMutations(employeeId?: string) {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['documents'] })

  return {
    save: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<EmployeeDocumentView>(`/v1/employees/${employeeId}/documents`, body)).data,
      onSuccess: invalidate,
    }),
    verify: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<EmployeeDocumentView>(`/v1/documents/${id}/verify`)).data,
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: async (id: string) => {
        await apiClient.delete(`/v1/documents/${id}`)
      },
      onSuccess: invalidate,
    }),
  }
}

export function useCompanyDocuments(legalEntityId: string | undefined) {
  return useQuery({
    queryKey: ['documents', 'company', legalEntityId],
    enabled: legalEntityId != null && legalEntityId !== '',
    queryFn: async () =>
      (await apiClient.get<CompanyDocumentView[]>('/v1/company-documents', {
        params: { legalEntityId },
      })).data,
  })
}

export function useCompanyDocumentMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['documents'] })

  return {
    save: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<CompanyDocumentView>('/v1/company-documents', body)).data,
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: async (id: string) => {
        await apiClient.delete(`/v1/company-documents/${id}`)
      },
      onSuccess: invalidate,
    }),
  }
}

/**
 * Everything expiring across a set of entities, for the dashboard widget.
 *
 * There is no server-side "expiring across the company" endpoint yet, so this fans out one
 * request per entity and merges. Honest about the cost: the widget takes a small list of
 * entities, not the whole group, and the alternative — inventing a query the API does not
 * have — would be a screen that silently shows one entity's documents under a heading that
 * claims all of them.
 */
export function useExpiringCompanyDocuments(legalEntityIds: string[]) {
  return useQueries({
    queries: legalEntityIds.map((id) => ({
      queryKey: ['documents', 'company', id],
      queryFn: async () =>
        (await apiClient.get<CompanyDocumentView[]>('/v1/company-documents', {
          params: { legalEntityId: id },
        })).data,
    })),
    combine: (results) => ({
      isPending: results.some((result) => result.isPending),
      isError: results.some((result) => result.isError),
      data: results.flatMap((result) => result.data ?? []),
    }),
  })
}
